import { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./ui/Button";
import { Field } from "./ui/Field";
import { PALETTE } from "../lib/palette";
import { mindmapUrl, safeUrl } from "../lib/links";
import type { Project, ProjectDraft } from "../types";

interface Props {
  project: Project | null;
  takenColors: string[];
  onSave: (draft: ProjectDraft) => void;
  onClose: () => void;
}

export function ProjectDialog({ project, takenColors, onSave, onClose }: Props) {
  const [name, setName] = useState(project?.name ?? "");
  const [color, setColor] = useState(
    project?.color ??
      PALETTE.find((c) => !takenColors.includes(c)) ??
      PALETTE[0]
  );
  const [mindmap, setMindmap] = useState(project?.mindmap ?? "");
  const [taskFile, setTaskFile] = useState(project?.taskFile ?? "");
  const [driveFolder, setDriveFolder] = useState(project?.driveFolder ?? "");
  const [error, setError] = useState("");

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give the project a name.");
      return;
    }
    if (mindmap.trim() && !mindmapUrl(mindmap)) {
      setError(
        "The mindmap must be a full http or https link, or a plain filename."
      );
      return;
    }
    // Links come straight back out of the sheet into a window.open, so they
    // are vetted here rather than at the point of use.
    for (const [label, value] of [
      ["task file", taskFile],
      ["project drive", driveFolder],
    ] as const) {
      if (value.trim() && !safeUrl(value)) {
        setError(`The ${label} link must be a full http or https address.`);
        return;
      }
    }
    onSave({
      name: trimmed,
      color,
      archived: project?.archived ?? false,
      mindmap: mindmap.trim(),
      taskFile: taskFile.trim(),
      driveFolder: driveFolder.trim(),
    });
  };

  return (
    <Modal
      title={project ? "Edit project" : "New project"}
      subtitle={
        project ? undefined : "Tasks belong to a project, so this comes first."
      }
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            {project ? "Save changes" : "Create project"}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <input
          className="input"
          placeholder="YouthBridge"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </Field>
      <div className="field">
        <span className="field__label">Colour</span>
        <div className="swatches">
          {PALETTE.map((c) => (
            <button
              key={c}
              className="swatch"
              style={{ background: c }}
              aria-pressed={c === color}
              aria-label={`Colour ${c}`}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
      </div>
      <Field label="Mindmap link or filename (optional)">
        <input
          className="input"
          placeholder="https://my-maps.netlify.app/YouthBridge.html"
          value={mindmap}
          onChange={(e) => setMindmap(e.target.value)}
        />
      </Field>
      <Field label="Task file link (optional)">
        <input
          className="input"
          type="url"
          placeholder="https://docs.google.com/spreadsheets/d/..."
          value={taskFile}
          onChange={(e) => setTaskFile(e.target.value)}
        />
      </Field>
      <Field label="Project drive link (optional)">
        <input
          className="input"
          type="url"
          placeholder="https://drive.google.com/drive/folders/..."
          value={driveFolder}
          onChange={(e) => setDriveFolder(e.target.value)}
        />
      </Field>
      <div className="err">{error}</div>
    </Modal>
  );
}
